import { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import ApiHelper from "../../../Helpers/ApiHelper";
import { SetProfilePictureModalIsShown, SetReduxProfilePicture } from "../../AppMenu/Store";
import Image from "react-bootstrap/Image";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { folderEnum } from "../../../Helpers/EnumHelper";
import IconSvg from "../../SharedComponents/IconSvg";
import { deleteProfilePic } from "../../AppMenu/Store/Thunk";
import { useTranslation } from "react-i18next";
import { UpdateAlertModalData } from "../../SharedComponents/GeneralModals/Store"; // i18n

const ProfilePictureModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isShown = useSelector((state) => state.appMenu.profilePictureModalIsShown);
  const windowSizeCssClass = useSelector((state) => state.appMenu.screenSize);
  const reduxProfilePicture = useSelector((state) => state.appMenu.reduxProfilePicture);
  const user = useSelector((state) => state.auth?.user);

  const [userPhoto, setUserPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [isTakingAPhoto, setIsTakingAPhoto] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [alertData, setAlertData] = useState({ isShown: false, header: "", text: "" });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (userPhoto) {
      const objectUrl = URL.createObjectURL(userPhoto);
      setPreview(objectUrl);
      setShowSaveButton(true);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setShowSaveButton(false);
      setPreview(null);
    }
  }, [userPhoto]);

  useEffect(() => {
    isShown &&
      dispatch(
        UpdateAlertModalData({
          isShown: false,
          header: "",
          text: "",
        })
      );
  }, [isShown]);

  const UpdateProfilePic = async (photo) => {
    const formData = new FormData();
    formData.append("UserId", user.userId);
    formData.append("Photo", photo);
    await ApiHelper.xmlPostFormData("user/UpdateProfilePic", formData, user.token, (res) => {
      if (JSON.parse(res.response).statusCode === 1) {
        dispatch(SetReduxProfilePicture(URL.createObjectURL(photo)));
        setPreview(null);
        setUserPhoto(null);
      } else {
        setPreview(null);
        setUserPhoto(null);
        dispatch(
          UpdateAlertModalData({
            isShown: true,
            header: "serverError",
            text: "errorSettingProfilePicture",
          })
        );
      }
    });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUserPhoto(file);
      setIsUploaded(true);
    }
  };

  const startCamera = async () => {
    try {
      if (navigator.permissions) {
        const permissionStatus = await navigator.permissions.query({ name: "camera" });
        if (permissionStatus.state === "denied") {
          dispatch(
            UpdateAlertModalData({
              isShown: true,
              header: "cameraError",
              text: "cameraAccessBlockedAlert",
            })
          );
          return;
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      streamRef.current = stream;
    } catch (error) {
      console.error("Error accessing webcam:", error);
      if (error.name === "NotAllowedError") {
        dispatch(
          UpdateAlertModalData({
            isShown: true,
            header: "cameraError",
            text: "cameraDeniedAlert",
          })
        );
      } else if (error.name === "NotFoundError") {
        dispatch(
          UpdateAlertModalData({
            isShown: true,
            header: "cameraError",
            text: "noCameraFoundAlert",
          })
        );
      } else if (error.name === "NotReadableError") {
        dispatch(
          UpdateAlertModalData({
            isShown: true,
            header: "cameraError",
            text: "cameraInUseAlert",
          })
        );
      } else {
        dispatch(
          UpdateAlertModalData({
            isShown: true,
            header: "cameraError",
            text: "genericCameraErrorAlert",
          })
        );
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const capturePhoto = () => {
    if (!canvasRef.current || !videoRef.current) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const video = videoRef.current;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    const cropSize = Math.min(videoWidth, videoHeight);
    const startX = (videoWidth - cropSize) / 2;
    const startY = (videoHeight - cropSize) / 2;

    canvas.width = 350;
    canvas.height = 350;

    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(video, startX, startY, cropSize, cropSize, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      const file = new File([blob], "profile_picture.jpg", { type: "image/jpeg" });
      setUserPhoto(file);
      setIsTakingAPhoto(false);
      stopCamera();
    }, "image/jpeg");
  };

  const savePhoto = () => {
    UpdateProfilePic(userPhoto);
    setShowSaveButton(false);
  };

  const DeleteProfilePic = () => {
    setUserPhoto(null);
    dispatch(deleteProfilePic());
  };

  return (
    <>
      <Modal
        size={"lg"}
        show={isShown}
        onHide={() => {
          dispatch(SetProfilePictureModalIsShown(false));
          setIsTakingAPhoto(false);
          stopCamera();
        }}
        dialogClassName={`custom-modal-dialog ${windowSizeCssClass}`}
        contentClassName={"custom-modal-content"}
      >
        <div className={"bordered text-center"}>
          <Modal.Header className={"border-bottom-0"} closeButton></Modal.Header>
          <Row>
            <Col className={"position-relative"}>
              <div
                className={"rounded-circle border border-0 border-warning overflow-hidden mx-auto"}
                style={{ width: "50%", aspectRatio: 1, position: "relative" }}
              >
                {isTakingAPhoto ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    className="h-100 w-100"
                    style={{ objectFit: "cover", transform: "scaleX(-1)" }}
                  ></video>
                ) : (
                  <Image
                    src={
                      preview ||
                      reduxProfilePicture ||
                      (user.profilePic
                        ? ApiHelper.cloudFrontURL + folderEnum.profile + user.profilePic
                        : user.googlePic
                          ? user.googlePic
                          : `https://api.dicebear.com/9.x/bottts/svg?seed=${user.userId}&backgroundColor=ffffff`)
                    }
                    alt="profile"
                    className={"h-100 w-100 bg-transparent"}
                  />
                )}
              </div>
            </Col>
          </Row>

          <Row className={" pt-3 mt-3"} style={{ borderTop: "solid 2px #efefef" }}>
            <Col xs={"auto"}>
              <Button
                className={"button primary "}
                disabled={isTakingAPhoto || Boolean(preview)}
                onClick={() => {
                  setIsTakingAPhoto(true);
                  startCamera();
                }}
              >
                <Row className={"mx-0 justify-content-center"}>
                  <Col xs={"auto"}>
                    <IconSvg icon={"photoPlus"} fill={"#ffffff"}></IconSvg>
                  </Col>
                </Row>
                <Row className={"mx-0"}>
                  {preview && !isUploaded ? t("retakePhotoButtonLabel") : t("snapPhotoButtonLabel")}
                </Row>
              </Button>
            </Col>

            <Col xs={"auto"}>
              <Button
                className={"button primary "}
                disabled={isTakingAPhoto || Boolean(preview)}
                onClick={() => document.getElementById("profile-pic-upload").click()}
              >
                <Row className={"mx-0 justify-content-center"}>
                  <Col xs={"auto"}>
                    <IconSvg height={24} width={24} icon={"upload"} fill={"#ffffff"}></IconSvg>
                  </Col>
                </Row>
                <Row className={"mx-0"}>{t("uploadButtonLabel")}</Row>
              </Button>
            </Col>

            {(user.profilePic || reduxProfilePicture) && (
              <Col xs={"auto"}>
                <Button
                  className={"button secondary danger "}
                  disabled={isTakingAPhoto || Boolean(preview)}
                  onClick={DeleteProfilePic}
                >
                  <Row className={"mx-0 justify-content-center "}>
                    <Col xs={"auto"}>
                      <IconSvg height={24} width={24} icon={"delete"} fill={"currentcolor"}></IconSvg>
                    </Col>
                  </Row>
                  <Row className={"mx-0 justify-content-center"}>{t("deleteButtonLabel")}</Row>
                </Button>
              </Col>
            )}

            <Col></Col>
            <Col xs={"auto"} className={"align-content-end"}>
              {isTakingAPhoto && (
                <Row>
                  <Col xs={"auto"}>
                    <Button
                      className={"button secondary danger"}
                      onClick={() => {
                        setIsTakingAPhoto(false);
                        setPreview(null);
                        setUserPhoto(null);
                        stopCamera();
                      }}
                    >
                      {t("cancelPhotoButtonLabel")}
                    </Button>
                  </Col>
                  <Col xs={"auto"}>
                    <Button className={"button primary primary-color"} onClick={capturePhoto}>
                      {t("capturePhotoButtonLabel")}
                    </Button>
                  </Col>
                </Row>
              )}
              {preview && (
                <Row>
                  <Col xs={"auto"}>
                    {!isTakingAPhoto && preview && (
                      <Button
                        className={"button secondary danger"}
                        onClick={() => {
                          setIsTakingAPhoto(false);
                          setPreview(null);
                          setUserPhoto(null);
                          stopCamera();
                        }}
                      >
                        {t("clearProfilePicButtonLabel")}
                      </Button>
                    )}
                  </Col>
                  <Col xs={"auto"}>
                    {showSaveButton && (
                      <Button className={"button primary primary-color"} onClick={savePhoto}>
                        {t("savePhotoButtonLabel")}
                      </Button>
                    )}
                  </Col>
                </Row>
              )}
            </Col>
          </Row>

          <input
            id="profile-pic-upload"
            type="file"
            accept=".jpeg, .png, .webp, .jpg"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />

          <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
        </div>
      </Modal>
    </>
  );
};

export default ProfilePictureModal;
